/**
 * useMentionAutocomplete Hook
 * Feature: 036-markdown-critique-comments (User Story 3)
 * T031, T037: Autocomplete logic for @-mentions in critique comments
 * Extended for 050-critique-markup-integration: Shape reference autocomplete
 *
 * Provides photo and shape suggestions when typing "@" in comment fields.
 * Prioritizes photos from current collection/critique context.
 * Shows shape references (@1-@9) from attached markup with tool icons.
 */

import { useState, useMemo } from 'react';
import { useMentionAutocompleteGraphQL, useSessionParticipantsGraphQL } from '../graphql/hooks/useUtilityGraphQL';
import type { MarkupShape } from '@/types/markup.types';
import { getToolMetadata } from '@/utils/markupToolRegistry';

// ============================================================================
// Types
// ============================================================================

export interface MentionAutocompleteOption {
  /** Global object ID (e.g., "ph1a") or shape number (e.g., "1") */
  objectId: string;

  /** Object type */
  type: 'photos' | 'users' | 'collections' | 'shapes';

  /** Display label */
  label: string;

  /** Photo thumbnail URL (for photos) */
  thumbnailUrl?: string;

  /** Tool icon component (for shapes) */
  icon?: React.ComponentType<{ className?: string }>;

  /** Additional metadata */
  metadata?: {
    username?: string;
    photoCount?: number;
    shapeType?: string;
  };
}

export interface MentionAutocompleteState {
  /** Currently visible options after filtering */
  options: MentionAutocompleteOption[];

  /** Loading state */
  isLoading: boolean;

  /** Error state */
  error: Error | null;

  /** Filter by partial input (e.g., "ph" or "ph1") */
  setFilter: (partial: string) => void;

  /** Current filter value */
  filter: string;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Fetch autocomplete options for @-mentions
 *
 * @param collectionId - Collection context for photo suggestions
 * @param options - Configuration options
 * @returns Autocomplete state and options
 */
export function useMentionAutocomplete(
  collectionId: string | undefined,
  options: {
    /** Maximum number of options to return */
    limit?: number;

    /** Enable/disable the hook */
    enabled?: boolean;

    /** Attached markup shapes for shape reference suggestions (050) */
    attachedMarkup?: MarkupShape[];

    /** Feature 071: Session ID for participant suggestions */
    sessionId?: string;

    /** Feature 071: Current user ID to exclude from participant suggestions */
    currentUserId?: string;
  } = {}
): MentionAutocompleteState {
  const { limit = 10, enabled = true, attachedMarkup = [], sessionId, currentUserId } = options;

  const [filter, setFilter] = useState('');

  // Fetch photos from collection using GraphQL (Feature 065: Migrated from TanStack Query)
  const graphqlResult = useMentionAutocompleteGraphQL(collectionId, {
    limit: 100, // Fetch more to allow filtering
    enabled: enabled && !!collectionId,
  });

  // Feature 071: Fetch session participants for @-mention suggestions
  const participantsResult = useSessionParticipantsGraphQL(sessionId, {
    enabled: enabled && !!sessionId,
    excludeUserId: currentUserId, // Don't suggest the current user
  });

  const { options: graphqlOptions, isLoading: photosLoading, error: photosError } = graphqlResult;
  const { participants, isLoading: participantsLoading, error: participantsError } = participantsResult;

  // Combine loading and error states
  const isLoading = photosLoading || participantsLoading;
  const error = photosError || participantsError;

  // Convert GraphQL options to autocomplete options with shape and participant support
  const allOptions = useMemo<MentionAutocompleteOption[]>(() => {
    const photoOptions: MentionAutocompleteOption[] = graphqlOptions.map((opt) => ({
      objectId: opt.objectId,
      type: opt.type,
      label: opt.label,
      thumbnailUrl: opt.thumbnailUrl,
    }));

    // Convert attached markup shapes to autocomplete options (050)
    const shapeOptions: MentionAutocompleteOption[] = attachedMarkup
      .sort((a, b) => a.number - b.number) // Sort by shape number
      .map((shape) => {
        const toolMetadata = getToolMetadata(shape.type);
        return {
          objectId: String(shape.number), // "1", "2", etc.
          type: 'shapes' as const,
          label: `${toolMetadata?.label || shape.type} ${shape.number}`,
          icon: toolMetadata?.icon,
          metadata: {
            shapeType: shape.type,
          },
        };
      });

    // Feature 071: Convert session participants to autocomplete options
    const participantOptions: MentionAutocompleteOption[] = participants.map((p) => ({
      objectId: p.username, // Use username for @-mention insertion
      type: 'users' as const,
      label: p.displayName || p.username,
      metadata: {
        username: p.username,
      },
    }));

    // Order: Shapes first, then participants, then photos
    return [...shapeOptions, ...participantOptions, ...photoOptions];
  }, [graphqlOptions, attachedMarkup, participants]);

  // Filter options based on user input
  const filteredOptions = useMemo(() => {
    if (!filter) {
      // No filter: return first N options (shapes first, then photos)
      return allOptions.slice(0, limit);
    }

    const lowerFilter = filter.toLowerCase();

    // Check if filter is a digit (shape reference: 1-9)
    if (/^\d+$/.test(filter)) {
      // Filter shape options by number
      return allOptions
        .filter(opt => opt.type === 'shapes' && opt.objectId.startsWith(filter))
        .slice(0, limit);
    }

    // Check if filter is a partial global ID (e.g., "ph" or "ph1")
    if (lowerFilter.startsWith('ph')) {
      // Filter by object ID prefix
      return allOptions
        .filter(opt => opt.objectId.toLowerCase().startsWith(lowerFilter))
        .slice(0, limit);
    }

    // Otherwise, filter by label or username (starts with for better UX)
    // First try exact prefix matches (higher priority)
    const prefixMatches = allOptions.filter(opt =>
      opt.label.toLowerCase().startsWith(lowerFilter) ||
      (opt.type === 'users' && opt.objectId.toLowerCase().startsWith(lowerFilter))
    );

    if (prefixMatches.length > 0) {
      return prefixMatches.slice(0, limit);
    }

    // Fall back to partial matches if no prefix matches found
    return allOptions
      .filter(opt =>
        opt.label.toLowerCase().includes(lowerFilter) ||
        (opt.type === 'users' && opt.objectId.toLowerCase().includes(lowerFilter))
      )
      .slice(0, limit);
  }, [allOptions, filter, limit]);

  return {
    options: filteredOptions,
    isLoading,
    error: error as Error | null,
    setFilter,
    filter,
  };
}

// ============================================================================
// Helper: Detect mention trigger in text
// ============================================================================

export interface MentionTrigger {
  /** Whether a mention trigger was detected */
  triggered: boolean;

  /** The partial input after "@" (e.g., "ph" or "ph1a") */
  partial: string;

  /** Start position of the "@" character */
  startPos: number;

  /** Expected object type based on prefix (if recognizable) */
  expectedType?: 'photos' | 'users' | 'collections';
}

/**
 * Detect if user is typing a @-mention at current cursor position
 *
 * @param text - Full text content
 * @param cursorPos - Current cursor position
 * @returns Mention trigger information
 */
export function detectMentionTrigger(
  text: string,
  cursorPos: number
): MentionTrigger {
  // Find the last "@" before cursor
  const beforeCursor = text.substring(0, cursorPos);
  const lastAtIndex = beforeCursor.lastIndexOf('@');

  // No "@" found, or "@" is at the very start
  if (lastAtIndex === -1) {
    return { triggered: false, partial: '', startPos: -1 };
  }

  // Extract text between "@" and cursor
  const partial = beforeCursor.substring(lastAtIndex + 1);

  // Check if there's whitespace after "@" (if so, not a mention)
  if (/\s/.test(partial)) {
    return { triggered: false, partial: '', startPos: -1 };
  }

  // Valid mention trigger
  const trigger: MentionTrigger = {
    triggered: true,
    partial,
    startPos: lastAtIndex,
  };

  // Detect expected type from prefix
  if (partial.startsWith('ph')) {
    trigger.expectedType = 'photos';
  } else if (partial.startsWith('us')) {
    trigger.expectedType = 'users';
  } else if (partial.startsWith('co')) {
    trigger.expectedType = 'collections';
  }

  return trigger;
}

// ============================================================================
// Helper: Insert mention at cursor position
// ============================================================================

export interface InsertMentionResult {
  /** Updated text with mention inserted */
  newText: string;

  /** New cursor position after insertion */
  newCursorPos: number;
}

/**
 * Insert a mention object ID at the trigger position
 *
 * @param text - Current text content
 * @param trigger - Mention trigger information
 * @param objectId - Global object ID to insert (e.g., "ph1a")
 * @returns Updated text and cursor position
 */
export function insertMention(
  text: string,
  trigger: MentionTrigger,
  objectId: string
): InsertMentionResult {
  if (!trigger.triggered) {
    return { newText: text, newCursorPos: text.length };
  }

  // Replace from "@" to current partial with "@objectId "
  const before = text.substring(0, trigger.startPos);
  const after = text.substring(trigger.startPos + 1 + trigger.partial.length);
  const newText = `${before}@${objectId} ${after}`;

  // Position cursor after inserted mention and space
  const newCursorPos = trigger.startPos + objectId.length + 2; // +2 for "@" and space

  return { newText, newCursorPos };
}
